import {
  Button,
  Checkbox,
  Container,
  Dialog,
  DialogContent,
  DialogContentText,
  FormControlLabel,
  TextField,
  Typography
} from "@material-ui/core";
import BorderAllIcon from "@material-ui/icons/BorderAll";
import {
  useTemplateDelete,
  useTemplateDuplicate,
  useTemplateFinalize,
  useTemplateRename
} from "@saleor/dropOff/mutation";
import { useGetAvailableTemplatesQuery } from "@saleor/dropOff/queries";
import { useTemplateStyles } from "@saleor/dropOff/styles/templates";
import { MallListDropOff_malls_edges_node } from "@saleor/dropOff/types/MallListDropOff";
import { dropOffEditPath, dropOffPath } from "@saleor/dropOff/url";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import { renderCollection } from "@saleor/misc";
import { TemplateStatus } from "@saleor/types/globalTypes";
import { handleGraphQLErrors } from "@saleor/utils/handlers/errorHandler";
import { stringifyQs } from "@saleor/utils/urls";
import * as React from "react";
import urlJoin from "url-join";

import { DropOffHomeLocOutletIndicatorEnum } from "../DropOffHomeLocLabels";
import DropOffTemplateListCard from "./DropOffTemplateListCard";

export interface DropOffTemplateProps {
  mallId: string;
  mall: MallListDropOff_malls_edges_node;
  disableAction: boolean;
  filterOutletStatus: number;
}

const DropOffTemplate: React.FC<DropOffTemplateProps> = props => {
  const { mallId, mall, disableAction, filterOutletStatus } = props;

  const classes = useTemplateStyles({});
  const notify = useNotifier();
  const navigate = useNavigator();

  interface DialogProps {
    show: boolean;
    title: string;
    contentText: string;
    onYes: (val: string, val2?: boolean) => void;
    onCancel: () => void;
    showTextField: boolean;
    confirmText?: string;
    contentTextAlign?: string;
    showCheckboxField?: boolean;
    isChecked?: boolean;
  }

  const [displayDialog, setDisplayDialog] = React.useState(false);
  const [dialogTitle, setDialogTitle] = React.useState("");
  const [dialogYesClicked, setDialogYesClicked] = React.useState<any>();
  const [dialogCancelClicked, setDialogCancelClicked] = React.useState<any>();
  const [dialogShowTextField, setDialogShowTextField] = React.useState(false);
  const [dialogContentText, setDialogContentText] = React.useState("");
  const [dialogTextInputText, setDialogTextInputText] = React.useState("");
  const [dialogConfirmText, setDialogConfirmText] = React.useState("YES");
  const [dialogContentTextAlign, setDialogContentTextAlign] = React.useState(
    "center"
  );
  const [dialogShowCheckboxField, setDialogShowCheckboxField] = React.useState(
    false
  );
  const [dialogCheckboxValue, setDialogCheckboxValue] = React.useState(false);
  const [
    dialogCheckboxFieldDisabled,
    setDialogCheckboxFieldDisabled
  ] = React.useState(false);
  const [proceedToFinalize, setProceedToFinalize] = React.useState(false);
  const [filteredTemplates, setFilteredTemplates] = React.useState([]);

  const showDialog = ({
    show,
    title,
    contentText,
    onYes,
    onCancel,
    showTextField,
    confirmText,
    contentTextAlign,
    showCheckboxField,
    isChecked
  }: DialogProps) => {
    setDisplayDialog(show);
    setDialogTitle(title);
    setDialogYesClicked({ func: onYes });
    setDialogCancelClicked({ func: onCancel });
    setDialogShowTextField(showTextField);
    setDialogContentText(contentText);
    setDialogConfirmText(confirmText ?? "YES");
    setDialogContentTextAlign(contentTextAlign ?? "center");
    setDialogShowCheckboxField(showCheckboxField);
    setDialogCheckboxValue(isChecked);
    setDialogCheckboxFieldDisabled(isChecked);
    setProceedToFinalize(false);
  };

  const {
    data: templates,
    refetch: refetchTemplates,
    loading: templatesLoading
  } = useGetAvailableTemplatesQuery({
    displayLoader: true,
    variables: {
      filter: { mall: mallId },
      first: 100
    }
  });
  const availableTemplates = templates?.templates?.edges.map(edge => edge.node);

  React.useEffect(() => {
    if (availableTemplates) {
      setFilteredTemplates(
        renderCollection(availableTemplates, item => {
          const homeLocsWithOutletId = item?.dropOffLocations?.edges?.filter(
            edge => edge?.node?.outletId
          );

          let statusReg = 0;
          if (
            item?.dropOffLocations?.totalCount === homeLocsWithOutletId.length
          ) {
            statusReg = DropOffHomeLocOutletIndicatorEnum.REGISTERED;
          } else if (homeLocsWithOutletId.length > 0) {
            statusReg = DropOffHomeLocOutletIndicatorEnum.INCOMPLETE;
          } else {
            statusReg = DropOffHomeLocOutletIndicatorEnum.UNREGISTERED;
          }

          return {
            ...item,
            statusReg
          };
        })
      );
    }
  }, [templates?.templates?.edges]);

  const [duplicateTemplate] = useTemplateDuplicate({
    onCompleted: e => {
      if (e.templateDuplicate.taskAreaError.length === 0) {
        notify({
          status: "success",
          text: "Duplicate Success!"
        });
        refetchTemplates();
      } else {
        notify({
          status: "error",
          text: e.templateDuplicate.taskAreaError[0].message
        });
      }
    },
    onError: handleGraphQLErrors
  });

  const [deleteTemplate] = useTemplateDelete({
    onCompleted: e => {
      if (e.templateDelete.templateError.length === 0) {
        notify({
          status: "success",
          text: "Template Deleted."
        });
        refetchTemplates();
      } else {
        notify({
          status: "error",
          text: e.templateDelete.templateError[0].message
        });
      }
    },
    onError: handleGraphQLErrors
  });

  const [renameTemplate] = useTemplateRename({
    onCompleted: e => {
      if (e.templateRename.taskAreaError.length === 0) {
        if (proceedToFinalize) {
          finalizeTemplate({
            variables: {
              id: e.templateRename.template.id
            }
          });
        } else {
          notify({
            status: "success",
            text: "Rename Successful."
          });
          refetchTemplates();
          if (displayDialog) {
            setDisplayDialog(false);
          }
        }
      } else {
        notify({
          status: "error",
          text: e.templateRename.taskAreaError[0].message
        });
      }
    },
    onError: handleGraphQLErrors
  });

  const [finalizeTemplate] = useTemplateFinalize({
    onCompleted: e => {
      if (e.templateFinalize.templateError.length === 0) {
        if (proceedToFinalize) {
          notify({
            status: "success",
            text: "Rename Successful."
          });
        }
        notify({
          status: "success",
          text: "Active Template Update Successful."
        });
        refetchTemplates();
        if (displayDialog) {
          setDisplayDialog(false);
        }
      } else {
        notify({
          status: "error",
          text: e.templateFinalize.templateError[0].message
        });
      }
    },
    onError: handleGraphQLErrors
  });

  const isTimeDuringMallHours = checkMall => {
    const today = new Date();
    const [hours, minutes] = checkMall.mallOpening.split(":");
    const mallOpeningTime = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
      hours,
      minutes
    );
    const [closingHrs, closingMins] = checkMall.mallClosing.split(":");
    const mallClosingTime = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
      closingHrs,
      closingMins
    );

    const currentTime = today.getTime();
    const openingTime = mallOpeningTime.getTime();
    const closingTime = mallClosingTime.getTime();

    return currentTime >= openingTime && currentTime <= closingTime;
  };

  const handleDuplicate = templateId => {
    const nonDraftTemplates = availableTemplates.filter(
      template => template.status !== TemplateStatus.DRAFT
    );

    if (nonDraftTemplates.length >= 10) {
      notify({
        status: "error",
        text: "The maximum number of templates has been reached"
      });
    } else {
      duplicateTemplate({
        variables: {
          id: templateId
        }
      });
      setDisplayDialog(false);
    }
  };

  const handleEdit = (availableTemplate, templateId) => {
    if (availableTemplate.status === "ACTIVE" && isTimeDuringMallHours(mall)) {
      notify({
        status: "error",
        text: "Active Template cannot be edited"
      });
    } else {
      navigate(
        urlJoin(
          dropOffEditPath(templateId),
          "?" +
            stringifyQs({
              mall: mallId,
              step: 1
            })
        )
      );
    }
  };

  const setActiveTemplateClick = templateId => {
    finalizeTemplate({
      variables: {
        id: templateId
      }
    });
  };

  const renameSave = (templateId, inputText, checkValue, availableTemplate) => {
    setProceedToFinalize(
      checkValue === true && availableTemplate.status !== "ACTIVE"
    );
    renameTemplate({
      variables: {
        id: templateId,
        name: inputText
      }
    });
  };

  const getContentToRender = () => {
    let contentToRender = null;

    if (templatesLoading) {
      contentToRender = (
        <div className={classes.templateTitle}>
          <div className={classes.templateTitleNoAvailable}>
            <h4>Loading Templates</h4>
          </div>
        </div>
      );
    } else if (availableTemplates?.length === 0) {
      contentToRender = (
        <div className={classes.templateTitle}>
          <div className={classes.templateTitleNoAvailable}>
            <h4>No Templates Available</h4>
          </div>
        </div>
      );
    } else {
      contentToRender = (
        <>
          <div className={classes.templateTitle}>
            <div>
              <h4>Available Templates</h4>
            </div>
            <div className={classes.templateTitleActions}>
              <h4>Actions</h4>
            </div>
          </div>
          <div className={classes.templateItems}>
            {availableTemplates &&
              filteredTemplates
                .filter(
                  item =>
                    parseInt(filterOutletStatus.toString(), 10) === 0 ||
                    item.statusReg ===
                      parseInt(filterOutletStatus.toString(), 10)
                )
                .map(availableTemplate => (
                  <DropOffTemplateListCard
                    key={"template-" + availableTemplate?.id}
                    disableAction={disableAction}
                    templateDetails={availableTemplate}
                    filterOutletStatus={filterOutletStatus}
                    onDuplicate={templateId => {
                      showDialog({
                        contentText:
                          "Are you sure you want to duplicate this template?",
                        onCancel: () => {
                          setDisplayDialog(false);
                        },
                        onYes: () => {
                          handleDuplicate(templateId);
                        },
                        show: true,
                        showTextField: false,
                        title: "Duplicate Template Confirmation"
                      });
                    }}
                    onEdit={templateId => {
                      showDialog({
                        contentText:
                          "Are you sure you want to edit this template? Changing or deleting Task Areas will reset the store assignment.",
                        onCancel: () => {
                          setDisplayDialog(false);
                        },
                        onYes: () => {
                          handleEdit(availableTemplate, templateId);
                        },
                        show: true,
                        showTextField: false,
                        title: "Edit Template Confirmation"
                      });
                    }}
                    onDelete={templateId => {
                      showDialog({
                        contentText:
                          "Are you sure you want to delete this template? All store and runner assignments will not be saved. You cannot undo this process.",
                        onCancel: () => {
                          setDisplayDialog(false);
                        },
                        onYes: () => {
                          deleteTemplate({
                            variables: {
                              id: templateId
                            }
                          });
                          setDisplayDialog(false);
                        },
                        show: true,
                        showTextField: false,
                        title: "Delete Template Confirmation"
                      });
                    }}
                    onRename={(templateId, templateName) => {
                      setDialogTextInputText(templateName);
                      showDialog({
                        confirmText: "SAVE",
                        contentText: "Enter new name of template",
                        contentTextAlign: "left",
                        isChecked: availableTemplate.status === "ACTIVE",
                        onCancel: () => {
                          setDisplayDialog(false);
                        },
                        onYes: (inputText, checkValue) => {
                          renameSave(
                            templateId,
                            inputText,
                            checkValue,
                            availableTemplate
                          );
                        },
                        show: true,
                        showCheckboxField: true,
                        showTextField: true,
                        title: "Rename Template Confirmation"
                      });
                    }}
                    onView={(templateId, mallId) => {
                      navigate(
                        urlJoin(
                          dropOffPath(templateId),
                          "?" +
                            stringifyQs({
                              mall: mallId
                            })
                        )
                      );
                    }}
                    onSetActiveTemplate={templateId => {
                      setActiveTemplateClick(templateId);
                    }}
                  />
                ))}
          </div>
        </>
      );
    }
    return contentToRender;
  };

  return (
    <Container className={classes.dropOffContainer}>
      {getContentToRender()}

      <Dialog
        open={displayDialog}
        maxWidth="xs"
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            <div className={classes.divFlexCentered}>
              <BorderAllIcon color="primary" style={{ marginTop: 2 }} />

              <Typography className={classes.templateDialogTitle} variant="h5">
                {dialogTitle}
              </Typography>
            </div>

            <div
              className={
                dialogContentTextAlign === "center"
                  ? classes.centerContent
                  : classes.leftContent
              }
            >
              {dialogContentText}
            </div>

            {dialogShowTextField && (
              <div>
                <form noValidate autoComplete="off">
                  <TextField
                    label="Home Location Name"
                    value={dialogTextInputText}
                    variant="outlined"
                    style={{
                      overflow: "hidden",
                      width: "100%"
                    }}
                    onChange={e => {
                      if (e.currentTarget.value.length >= 150) {
                        return;
                      }
                      setDialogTextInputText(e.currentTarget.value);
                    }}
                  />
                </form>
              </div>
            )}

            {dialogShowCheckboxField && (
              <div>
                <form noValidate autoComplete="off">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={dialogCheckboxValue}
                        onChange={e => {
                          setDialogCheckboxValue(e.target.checked);
                        }}
                        color="primary"
                        disabled={dialogCheckboxFieldDisabled}
                      />
                    }
                    label="Set as Active"
                  />
                </form>
              </div>
            )}

            <div
              className={
                dialogContentTextAlign === "center"
                  ? classes.buttonActionCenter
                  : classes.buttonActionRight
              }
            >
              <Button
                color="primary"
                variant="outlined"
                className={classes.displayFlexMarginRight20}
                onClick={() => dialogCancelClicked.func()}
              >
                CANCEL
              </Button>
              <Button
                color="primary"
                variant="contained"
                style={{
                  backgroundColor:
                    dialogShowTextField && dialogTextInputText === ""
                      ? "#E4E4E4"
                      : "#0030FF"
                }}
                disabled={dialogShowTextField && dialogTextInputText === ""}
                onClick={() =>
                  dialogYesClicked.func(
                    dialogTextInputText,
                    dialogCheckboxValue
                  )
                }
              >
                <Typography className={classes.textWhite}>
                  {dialogConfirmText}
                </Typography>
              </Button>
            </div>
          </DialogContentText>
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default DropOffTemplate;
